import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useActiveProject } from './ActiveProjectContext';
import {
  useChatChannel,
  useChatMessagesCache,
  useChatUnread,
  useOpsChatPoller,
} from '../hooks/useChatQueries';

const AUDIO_COOLDOWN_MS = 2500;

const ChatNotificationContext = createContext(null);

function storageKey(userId, projectId) {
  return `ops-chat-last-seen:${userId}:${projectId}`;
}

function createChimePlayer() {
  if (typeof window === 'undefined') return { play: () => {}, unlock: () => {} };
  const Ctx = window.AudioContext || window.webkitAudioContext;
  let ctx = null;
  let unlocked = false;
  const unlock = async () => {
    if (!Ctx) return;
    try {
      if (!ctx) ctx = new Ctx();
      if (ctx.state === 'suspended') await ctx.resume();
      unlocked = ctx.state === 'running';
    } catch {
      unlocked = false;
    }
  };
  const play = async () => {
    if (!Ctx) return;
    if (!ctx || !unlocked) await unlock();
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.07, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.19);
    gain.connect(ctx.destination);
    const o1 = ctx.createOscillator();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(740, now);
    o1.connect(gain);
    o1.start(now);
    o1.stop(now + 0.11);
    const o2 = ctx.createOscillator();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(988, now + 0.095);
    o2.connect(gain);
    o2.start(now + 0.095);
    o2.stop(now + 0.19);
  };
  return { play, unlock };
}

export function ChatNotificationProvider({ children }) {
  const { user } = useAuth();
  const { activeProject } = useActiveProject();
  const location = useLocation();

  const [lastSeenMessageId, setLastSeenMessageId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );

  const projectId = activeProject?.project_id || null;
  const userId = user?.id || null;
  const isOpsChatRoute = location.pathname.startsWith('/operations/chat');

  const latestIdRef = useRef(null);
  const prevMessageIdRef = useRef(null);
  const lastSoundAtRef = useRef(0);
  const chimeRef = useRef(createChimePlayer());
  const fieldOpsChatPanelOpenRef = useRef(false);
  const opsChatScrollAtBottomRef = useRef(false);

  const chatEnabled = Boolean(projectId && userId);
  const { data: channel } = useChatChannel(projectId, { enabled: chatEnabled });
  const channelId = channel?.id ?? null;

  // El montaje pide un resumen —cuántos sin leer y cuál es el último id—, no el
  // historial. De cada mensaje aquí sólo se usaban `m.id` y `m.sender_id`; con
  // 600 mensajes eso eran 1.203 consultas y 94 KB para calcular un número.
  const { data: resumen } = useChatUnread(channelId, lastSeenMessageId, {
    enabled: chatEnabled && !!channelId,
  });
  const resumenVigente = resumen && resumen.canal === channelId ? resumen : null;
  const servidorNoLeidos = resumenVigente?.unread_count ?? 0;
  const servidorUltimoId = resumenVigente?.latest_id ?? null;

  // Lo que el poll va dejando en la caché (o el historial que haya pedido un
  // panel abierto por su cuenta). Aquí no se pide nada.
  const enCache = useChatMessagesCache(channelId);
  const llegados = useMemo(() => enCache || [], [enCache]);

  const arranque = useMemo(
    () => (resumenVigente ? { canal: channelId, desde: servidorUltimoId } : null),
    [resumenVigente, channelId, servidorUltimoId]
  );
  useOpsChatPoller(channelId, { enabled: chatEnabled && !!channelId, arranque });

  const ultimoLlegadoId = llegados.length ? llegados[llegados.length - 1]?.id ?? null : null;
  const latestMessageId =
    Math.max(ultimoLlegadoId || 0, servidorUltimoId || 0) || null;

  const setFieldOpsChatPanelOpen = useCallback((open) => {
    fieldOpsChatPanelOpenRef.current = Boolean(open);
  }, []);

  const setOpsChatScrollAtBottom = useCallback((atBottom) => {
    opsChatScrollAtBottomRef.current = Boolean(atBottom);
  }, []);

  const persistSeen = useCallback(
    (id) => {
      if (!userId || !projectId || id == null) return;
      try {
        localStorage.setItem(storageKey(userId, projectId), String(id));
      } catch {
        // ignore storage quota/privacy mode issues
      }
    },
    [userId, projectId]
  );

  const requestNotificationPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    try {
      const p = await Notification.requestPermission();
      setNotificationPermission(p);
      return p;
    } catch {
      return Notification.permission;
    }
  }, []);

  const unlockAudio = useCallback(async () => {
    await chimeRef.current.unlock();
    setAudioEnabled(true);
  }, []);

  const maybePlaySound = useCallback(async () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    const now = Date.now();
    if (now - lastSoundAtRef.current < AUDIO_COOLDOWN_MS) return;
    lastSoundAtRef.current = now;
    await chimeRef.current.play();
  }, []);

  const maybeNotifyBackground = useCallback(
    (incoming) => {
      if (typeof document === 'undefined' || !document.hidden) return;
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      const last = incoming[incoming.length - 1];
      const title = 'Operations Chat';
      const body =
        incoming.length > 1
          ? `${incoming.length} new messages in ${activeProject?.project_name || 'current project'}`
          : `${last.sender_name || 'New message'}: ${(last.body || '').slice(0, 80)}`;
      try {
        const n = new Notification(title, {
          body,
          tag: `ops-chat-${projectId || 'project'}`,
          renotify: true,
        });
        n.onclick = () => {
          window.focus();
        };
      } catch {
        // ignore browser-specific failures
      }
    },
    [activeProject?.project_name, projectId]
  );

  const markCurrentProjectChatSeen = useCallback(() => {
    if (latestIdRef.current == null) return;
    setLastSeenMessageId(latestIdRef.current);
    setUnreadCount(0);
    persistSeen(latestIdRef.current);
  }, [persistSeen]);

  useEffect(() => {
    if (!projectId || !userId) {
      setLastSeenMessageId(null);
      setUnreadCount(0);
      latestIdRef.current = null;
      prevMessageIdRef.current = null;
      return;
    }
    const raw = localStorage.getItem(storageKey(userId, projectId));
    const parsed = raw != null ? parseInt(raw, 10) : null;
    setLastSeenMessageId(Number.isFinite(parsed) ? parsed : null);
    prevMessageIdRef.current = null;
  }, [projectId, userId]);

  useEffect(() => {
    opsChatScrollAtBottomRef.current = false;
    fieldOpsChatPanelOpenRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    latestIdRef.current = latestMessageId;
  }, [latestMessageId]);

  // «Hasta dónde había leído» al montar lo dice el servidor. Sin esto, el primer
  // lote que trajera el poll no sonaría: `prevMessageIdRef` seguiría a `null` y
  // la rama de aviso exige un `prevId` anterior.
  useEffect(() => {
    if (servidorUltimoId != null && prevMessageIdRef.current == null) {
      prevMessageIdRef.current = servidorUltimoId;
    }
  }, [servidorUltimoId, channelId]);

  useEffect(() => {
    if (!userId) {
      prevMessageIdRef.current = null;
      return;
    }

    const activelyReading =
      !document.hidden &&
      opsChatScrollAtBottomRef.current &&
      (isOpsChatRoute || fieldOpsChatPanelOpenRef.current);

    if (activelyReading && latestMessageId != null) {
      setLastSeenMessageId(latestMessageId);
      setUnreadCount(0);
      persistSeen(latestMessageId);
      prevMessageIdRef.current = latestMessageId;
      return;
    }

    // El contador es el del servidor más lo que haya llegado después. El poll
    // arranca en `servidorUltimoId`, así que no hay solape que contar dos veces.
    if (resumenVigente) {
      const posteriores = llegados.filter(
        (m) =>
          (m?.id || 0) > (servidorUltimoId || 0) &&
          (m?.id || 0) > (lastSeenMessageId || 0) &&
          m?.sender_id !== userId
      ).length;
      setUnreadCount(servidorNoLeidos + posteriores);
    }

    const prevId = prevMessageIdRef.current;
    if (prevId != null && ultimoLlegadoId != null && ultimoLlegadoId > prevId) {
      const incoming = llegados.filter(
        (m) => (m?.id || 0) > prevId && m?.sender_id !== userId
      );
      if (incoming.length) {
        if (!document.hidden) {
          maybePlaySound();
        } else {
          maybeNotifyBackground(incoming);
        }
      }
      prevMessageIdRef.current = ultimoLlegadoId;
    }
  }, [
    isOpsChatRoute,
    lastSeenMessageId,
    latestMessageId,
    llegados,
    maybeNotifyBackground,
    maybePlaySound,
    persistSeen,
    resumenVigente,
    servidorNoLeidos,
    servidorUltimoId,
    ultimoLlegadoId,
    userId,
  ]);

  useEffect(() => {
    const unlock = () => {
      unlockAudio().catch(() => {});
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        requestNotificationPermission().catch(() => {});
      }
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('touchstart', unlock, true);
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('touchstart', unlock, true);
      window.removeEventListener('keydown', unlock, true);
    };
  }, [requestNotificationPermission, unlockAudio]);

  const value = useMemo(
    () => ({
      channelId,
      latestMessageId,
      lastSeenMessageId,
      unreadCount,
      hasUnreadOpsChat: unreadCount > 0,
      audioEnabled,
      notificationPermission,
      markCurrentProjectChatSeen,
      setFieldOpsChatPanelOpen,
      setOpsChatScrollAtBottom,
      requestNotificationPermission,
      unlockAudio,
    }),
    [
      audioEnabled,
      channelId,
      lastSeenMessageId,
      latestMessageId,
      markCurrentProjectChatSeen,
      setFieldOpsChatPanelOpen,
      setOpsChatScrollAtBottom,
      notificationPermission,
      requestNotificationPermission,
      unreadCount,
      unlockAudio,
    ]
  );

  return <ChatNotificationContext.Provider value={value}>{children}</ChatNotificationContext.Provider>;
}

export function useChatNotifications() {
  const ctx = useContext(ChatNotificationContext);
  if (!ctx) {
    throw new Error('useChatNotifications must be used within ChatNotificationProvider');
  }
  return ctx;
}
